import * as Yup from 'yup'
import { isEmpty } from 'lodash'
import { AzureOfferPlan, AzurePricingType } from '../../types/flyout'
import {
  getVmPlanTcvValue,
  hasVmMultiMeterDimensions,
  hasVmTcvPricing,
  isVmAbsolutePriceSameAsPublic,
} from '../../components/MicrosoftFormComponent/VmPlan/vmPlanUtils'
import { isNonEmptyNumber } from '@labrav/common'
import {
  emptyFieldMessage,
  missignFieldMessage,
  vmAbsolutePriceSameAsPublicMessage,
} from './messagesContants'
import { microsoftOfferAndMPPOValidationSchemaCommon } from './validateMicrosoftResellerAuthorization'
import { buildOfferRecipientsValidation } from './offerRecipientYupValidation'

const priceValidationMessage = 'Enter value (max 5 decimals, ≤100,000,000).'

const isValidVmPrice = (value?: number | null) => {
  if (value === null || value === undefined || !isNonEmptyNumber(value)) {
    return false
  }

  if (value <= 0 || value > 100000000) {
    return false
  }

  const decimalPlaces = (value.toString().split('.')[1] || '').length
  return decimalPlaces <= 5
}

const vmDiscountPercentageSchema = Yup.number()
  .nullable()
  .test('discount', 'Enter discount.', function (value) {
    return this.options?.context?.azurePricingType ===
      AzurePricingType.LABRA_PERCENTAGE
      ? !(value === null || value === undefined || !isNonEmptyNumber(value))
      : true
  })
  .test('discount-range', 'Enter valid discount.', function (value) {
    if (
      this.options?.context?.azurePricingType ===
        AzurePricingType.LABRA_PERCENTAGE &&
      value !== null &&
      value !== undefined &&
      isNonEmptyNumber(value)
    ) {
      return value >= 0 && value <= 100
    }
    return true
  })

const vmFilledPlanSchema = Yup.object().shape({
  labraPlanId: Yup.string().required('Select a plan.'),
  discountPercentage: vmDiscountPercentageSchema,
  pricing: Yup.object().test(
    'vm-plan-pricing',
    priceValidationMessage,
    function (pricing) {
      const plan = this.parent as AzureOfferPlan
      if (!plan?.labraPlanId) {
        return true
      }

      if (hasVmMultiMeterDimensions(plan)) {
        return true
      }

      if (
        this.options?.context?.azurePricingType ===
        AzurePricingType.LABRA_PERCENTAGE
      ) {
        return true
      }

      if (hasVmTcvPricing(plan)) {
        const tcv = getVmPlanTcvValue(plan)
        if (!isValidVmPrice(tcv)) {
          return this.createError({
            path: `${this.path}.recurrentPrice.prices[0].pricePerPaymentInUsd`,
            message: priceValidationMessage,
          })
        }

        if (
          isVmAbsolutePriceSameAsPublic({
            plan,
            product: this.options?.context?.product,
            absolutePrice: tcv,
          })
        ) {
          return this.createError({
            path: `${this.path}.recurrentPrice.prices[0].pricePerPaymentInUsd`,
            message: vmAbsolutePriceSameAsPublicMessage,
          })
        }
      }

      return true
    }
  ),
})

const vmPlansValidationSchema = Yup.array()
  .test('at-least-one-plan', 'Select a plan.', plans =>
    (plans || []).some(plan => !isEmpty(plan?.labraPlanId))
  )
  .of(
    Yup.lazy((plan: AzureOfferPlan) => {
      if (!plan?.labraPlanId) {
        return Yup.object().shape({})
      }

      return vmFilledPlanSchema
    }) as unknown as Yup.AnySchema
  )

const { plans: _plans, ...vmCommonSchemaFields } =
  microsoftOfferAndMPPOValidationSchemaCommon

const recipientMessages = {
  firstNameRequired: missignFieldMessage,
  lastNameRequired: missignFieldMessage,
  emailRequired: 'Please provide the email of your offer recipient. ',
  emailInvalid: 'Enter valid email.',
  duplicateEmail: 'Duplicate emails are not allowed.',
}

const validateVmMicrosoftPrivateOffer = (isSalesforceContext: boolean) =>
  Yup.object().shape({
    ...vmCommonSchemaFields,
    plans: vmPlansValidationSchema,
    preparedBy: isSalesforceContext
      ? Yup.string()
          .required(emptyFieldMessage)
          .email('Enter a valid email address.')
      : Yup.string().nullable(),
    billingAccountId: Yup.string().required(emptyFieldMessage),
    privateOfferName: Yup.string().required(emptyFieldMessage),
    offerStakeholders: isSalesforceContext
      ? Yup.array().optional().notRequired()
      : Yup.array()
          .of(
            Yup.object().shape({
              email: Yup.string().required('Email cannot be empty.'),
            })
          )
          .min(1, 'At least one stakeholder is required.'),
    offerRecipients: buildOfferRecipientsValidation(recipientMessages),
  })

export default validateVmMicrosoftPrivateOffer
